// Единая карточка «не удалось загрузить». До этого каждый экран собирал свой
// Alert: где-то без описания, где-то без кнопки «повторить», а на канбане —
// вообще самодельная карточка. Разметка одна, различаются только текст и retry.
import type { ReactElement, ReactNode } from 'react';
import { Alert, Button, Card, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

const { Title, Text } = Typography;

interface ErrorStateProps {
  /** Что именно не загрузилось: «Не удалось загрузить сделки». */
  title: string;
  /** Ошибка запроса; её текст уходит в описание. */
  error?: unknown;
  /** Показывается вместо текста ошибки, если она пустая. */
  fallbackDescription?: string;
  onRetry?: () => void;
  /** `page` — карточка по центру пустого экрана, `inline` — плашка в потоке. */
  variant?: 'inline' | 'page';
  className?: string;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return null;
}

export function ErrorState({
  title,
  error,
  fallbackDescription,
  onRetry,
  variant = 'inline',
  className,
}: ErrorStateProps): ReactElement {
  const { t } = useTranslation();
  const description = errorMessage(error) ?? fallbackDescription ?? t('crm.shared.retry_fallback');
  const retryButton: ReactNode = onRetry ? (
    <Button
      size="small"
      aria-label={t('crm.shared.retry')}
      icon={<ReloadOutlined />}
      onClick={onRetry}
    >
      {t('crm.shared.retry')}
    </Button>
  ) : null;

  if (variant === 'page') {
    return (
      <main
        className={className ?? 'mx-auto flex h-full max-w-3xl items-center justify-center p-6'}
      >
        <Card style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
          <Title level={4}>{title}</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
            {description}
          </Text>
          {onRetry ? (
            <Button
              type="primary"
              aria-label={t('crm.shared.retry')}
              icon={<ReloadOutlined />}
              onClick={onRetry}
            >
              {t('crm.shared.retry')}
            </Button>
          ) : null}
        </Card>
      </main>
    );
  }

  return (
    <Alert
      type="error"
      showIcon
      // antd 6 renamed the Alert prop: `message` is deprecated in favour of
      // `title`. Same slot, same markup — the deprecation warning was the only
      // difference (it fired on every error state).
      title={title}
      description={description}
      {...(retryButton ? { action: retryButton } : {})}
      {...(className ? { className } : {})}
    />
  );
}
